export const transformDataForFrontend = (hospitalData) => {
  return {
    hospitals: hospitalData?.map(({ name, location }) => ({
      name: name ?? "-",
      location: location ?? "-",
    })),
    facilities: [
      {
        id: "ownership",
        label: "Ownership",
        values: hospitalData?.map((h) => h?.facilities?.ownership ?? "-"),
      },
      {
        id: "facilityType",
        label: "Facility Type",
        values: hospitalData?.map((h) => h?.facilities?.facility_type ?? "-"),
      },
      {
        id: "geography",
        label: "Geography",
        values: hospitalData?.map((h) => h?.facilities?.geography ?? "-"),
      },
      {
        id: "hospitalBeds",
        label: "Hospital Beds",
        values: hospitalData?.map((h) => h?.facilities?.hospital_beds ?? "-"),
      },
      {
        id: "patientVolume",
        label: "Patient Volume",
        values: hospitalData?.map((h) => h?.facilities?.patient_volume ?? "-"),
      },
      {
        id: "hospitalArea",
        label: "Hospital Area",
        values: hospitalData.map((h) => formatAreaSmart(h.facilities?.hospital_area) ?? "-"),
      },
      {
        id: "discharges",
        label: "Discharges",
        values: hospitalData.map((h) =>
          h.facilities?.discharges != null ? addCommasToNumber(h.facilities.discharges.toFixed(0)) : "-"
        ),
      },
      {
        id: "admissions",
        label: "Admissions",
        values: hospitalData.map((h) => addCommasToNumber(h.facilities?.admissions) ?? "-"),
      },
      {
        id: "teachingStatus",
        label: "Teaching Status",
        values: hospitalData.map((h) => formatTeachingStatus(h.facilities?.teaching_status)),
      },
      {
        id: "contact",
        label: "Contact",
        values: hospitalData.map((h) => h.facilities?.contact ?? "-"),
      },
      {
        id: "npiNumber",
        label: "NPI Number",
        values: hospitalData.map((h) => h.facilities?.npi_number ?? "-"),
      },
      {
        id: "firmType",
        label: "Firm Type",
        values: hospitalData.map((h) => h.facilities?.firm_type ?? "-"),
      },
      {
        id: "numberEmployees",
        label: "Number of Employees",
        values: hospitalData.map((h) => addCommasToNumber(h.facilities?.number_employees) ?? "-"),
      },
      {
        id: "numOperatingRooms",
        label: "Number of Operating Rooms",
        values: hospitalData.map((h) => addCommasToNumber(h.facilities?.num_operating_rooms) ?? "-"),
      },
      {
        id: "hospital340BId",
        label: "340B ID Classification",
        values: hospitalData.map((h) => h.facilities?.hospital_340b_id_classification ?? "-"),
      },
      {
        id: "numberAffiliatedPhysicians",
        label: "Number of Affiliated Physicians",
        values: hospitalData.map((h) => addCommasToNumber(h.facilities?.number_affiliated_physicians) ?? "-"),
      },
      {
        id: "readmissionReductionPenaltyScore",
        label: "Readmission Reduction Penalty Score",
        values: hospitalData.map((h) => h.facilities?.readmission_reduction_penalty_score ?? "-"),
      },
      {
        id: "website",
        label: "Website",
        values: hospitalData.map((h) => h.facilities?.website ?? "-"),
      },
    ],
    financials: [
      {
        id: "gross-revenue",
        label: "Gross Revenue",
        values: hospitalData?.map(
          (h) => formatNumber(h?.financials?.gross_revenue) ?? "-",
        ),
        badge: hospitalData?.map((h) => h?.badges?.gross_revenue ?? "-"),
      },
      {
        id: "net-revenue",
        label: "Net Revenue",
        values: hospitalData?.map(
          (h) => formatNumber(h?.financials?.net_revenue) ?? "-",
        ),
      },
      {
        id: "cost-ratio",
        label: "Revenue to Cost Ratio",
        values: hospitalData?.map(
          (h) => h?.financials?.revenue_to_cost_ratio ?? "-",
        ),
      },
      {
        id: "bad-debt",
        label: "Bad Debt Percentage",
        values: hospitalData?.map(
          (h) => formatPercentage(h?.financials?.bad_debt_percentage) ?? "-",
        ),
        badge: hospitalData?.map((h) => h?.badges?.bad_debt_percentage ?? "-"),
      },
      {
        id: "net-patient-revenue",
        label: "Net Patient Revenue",
        values: hospitalData.map((h) => formatNumber(h.financials?.net_patient_revenue ?? "-")),
      },
      {
        id: "operating-income",
        label: "Operating Income",
        values: hospitalData.map((h) => formatNumber(h.financials?.operating_income ?? "-")),
      },
      {
        id: "total-operating-expenses",
        label: "Total Operating Expenses",
        values: hospitalData.map((h) => formatNumber(h.financials?.tot_operating_expenses ?? "-")),
      },
      {
        id: "net-income",
        label: "Net Income",
        values: hospitalData.map((h) => formatNumber(h.financials?.net_income ?? "-")),
      },
      {
        id: "net-income-margin",
        label: "Net Income Margin",
        values: hospitalData.map((h) => (h.financials?.net_income_margin && h.financials?.net_income_margin[0] !== '-'  ? `${h.financials?.net_income_margin}%` : "-")),
      },
      {
        id: "outpatient-revenue",
        label: "Outpatient Revenue",
        values: hospitalData.map((h) => formatNumber(h.financials?.outpatient_revenue ?? "-")),
      },
      {
        id: "revenue-oncology",
        label: "Net Revenue Oncology",
        values: hospitalData.map((h) => formatNumber(h.financials?.net_revenue_oncology ?? "-")),
      },
    ],
    affiliations: [
      {
        id: "medical-school-affiliation",
        label: "Medical School Affiliation",
        values: hospitalData?.map((h) => h?.medical_school_affiliation ?? "-"),
      },
    ],
    accreditations: [
      {
        id: "accreditations",
        label: "Accreditations",
        values: hospitalData?.map(hospital => splitAndClean(hospital.accreditations) ?? "-"),
      },
    ],
    oncology_focus: [
      {
        id: "radiation-therapy",
        label: "Radiation Therapy",
        values: hospitalData?.map((h) => addCommasToNumber(h?.oncology_focus?.radiation_therapy) ?? "-")
      },
      {
        id: "chemo-therapy",
        label: "Chemo Therapy",
        values: hospitalData?.map((h) => addCommasToNumber(h?.oncology_focus?.chemo_therapy) ?? "-")
      },
      {
        id: "surg-onc",
        label: "Surg Onc.",
        values: hospitalData?.map((h) => addCommasToNumber(h?.oncology_focus?.procedures) ?? "-")
      },
    ],
    // oncology_research: [
    //   {
    //     id: "tumor-board",
    //     label: "Tumor Board",
    //     values: hospitalData?.map((h) => h?.oncology_research?.tumor_board),
    //   },
    //   {
    //     id: "participation-in-clinical-trials",
    //     label: "Participation in clinical trials",
    //     values: hospitalData?.map(
    //       (h) => h?.oncology_research?.participation_in_clinical_trials,
    //     ),
    //   },
    //   {
    //     id: "research-affiliations",
    //     label: "Research affiliations",
    //     values: hospitalData?.map(
    //       (h) => h?.oncology_research?.research_affiliations,
    //     ),
    //   },
    // ],
    accessibility_inclusivity: [
      {
        id: "geographic-accessibility",
        label: "Geographic Accessibility",
        values: hospitalData?.map(
          (h) => h?.accessibility_inclusivity?.geographic_accessibility ?? "-",
        ),
      },
    ],
    quality_of_care: [
      // {
      //   id: "cms-rating",
      //   label: "CMS Rating",
      //   values: hospitalData?.map((h) => h?.quality_of_care?.CMS_rating),
      // },
      // {
      //   id: "HCAHPS-rating",
      //   label: "HCAHPS Rating",
      //   values: hospitalData?.map((h) => h?.quality_of_care?.HCAHPS_rating),
      // },
      // {
      //   id: "nsqip-rating",
      //   label: "NSQIP Rating",
      //   values: hospitalData?.map((h) => h?.quality_of_care?.NSQIP_rating),
      // },
      // {
      //   id: "OQI-rating",
      //   label: "OQI Rating",
      //   values: hospitalData?.map((h) => h?.quality_of_care?.OQI_rating),
      // },
      // {
      //   id: "vizient-rating",
      //   label: "Vizient Rating",
      //   values: hospitalData?.map((h) => h?.quality_of_care?.Vizient_rating),
      // },
      // {
      //   id: "oncology-mortality",
      //   label: "Oncology Mortality",
      //   values: hospitalData?.map(
      //     (h) => h?.quality_of_care?.oncology_mortality,
      //   ),
      // },
      // {
      //   id: "readmissions-rate",
      //   label: "Readmissions Rates",
      //   values: hospitalData?.map((h) => h?.quality_of_care?.readmissions_rate),
      // },
      // {
      //   id: "nccn",
      //   label: "NCCN",
      //   values: hospitalData?.map((h) => (h?.quality_of_care?.nccn ? "Yes" : "No")),
      // },
      {
        id: "nccn",
        label: "NCCN",
        values: hospitalData?.map((h) =>
          h?.quality_of_care?.nccn === undefined
            ? "-"
            : h.quality_of_care.nccn
            ? "Yes"
            : "No"
        ),
      },
      {
        id: "c4qi",
        label: "C4QI",
        values: hospitalData?.map((h) =>
          h?.quality_of_care?.c4qi === undefined
            ? "-"
            : h?.quality_of_care?.c4qi
            ? "Yes"
            : "No"
        ),
      },
      {
        id: "oncology-mortality",
        label: "Oncology Mortality",
        values: hospitalData?.map((h) =>
          h?.quality_of_care?.oncology_mortality === undefined
            ? "-"
            : formatQualityValue(h?.quality_of_care?.oncology_mortality)
        ),
      },
      {
        id: "readmissions-rate",
        label: "Readmissions Rates",
        values: hospitalData?.map((h) =>
          h?.quality_of_care?.readmissions_rate === undefined
            ? "-"
            : formatQualityValue(h?.quality_of_care?.readmissions_rate)
        ),
      },
      {
        id: "hcahps",
        label: "HCAHPS",
        values: hospitalData?.map((h) =>
          h?.quality_of_care?.hcahps === undefined
            ? "-"
            : formatQualityValue(h?.quality_of_care?.hcahps)
        ),
      },
    ],
    patient_experience: [
      {
        id: "communication-with-nurses-doctors",
        label: "Communication With Nurses/Doctors",
        values: hospitalData?.map((h) =>
          emptyToDash(h?.patient_experience?.communication_with_nurses_doctors),
        ),
      },
      {
        id: "appointment-wait-times",
        label: "Appointment Wait Times",
        values: hospitalData?.map((h) =>
          emptyToDash(h?.patient_experience?.appointment_wait_times),
        ),
      },
      {
        id: "cleanliness-and-quietness",
        label: "Cleanliness and Quietness",
        values: hospitalData?.map((h) =>
          emptyToDash(h?.patient_experience?.cleanliness_and_quietness),
        ),
      },
      {
        id: "likelihood-of-recommendation",
        label: "Likelihood of Recommendation",
        values: hospitalData?.map((h) =>
          emptyToDash(h?.patient_experience?.likelihood_of_recommendation),
        ),
      },
      {
        id: "cancer-navigation-program",
        label: "Cancer Navigation Program",
        values: hospitalData?.map((h) =>
          h?.patient_experience?.cancer_navigation_program === undefined
            ? "-"
            : h?.patient_experience?.cancer_navigation_program
            ? "Yes"
            : "No"
        ),
      },
    ],
  };
};

function formatNumber(input) {
  if (!input || input === "-" || input === "$-" || input === "" || input == "$$0.00" || input === "-%") return "-";

  // Step 1: Remove all non-digit/minus/dot characters
  let numStr;
  if (input)
    numStr = input?.replace(/[^0-9-.]/g, "");

  // Step 2: Handle Indian comma system (e.g., "70,25,24,800.00" → "702524800.00")
  if (numStr.includes(",")) {
    numStr = numStr?.replace(/,/g, "");
  }

  // Step 3: Split into integer and decimal parts
  const [integerPart, decimalPart] = numStr.split(".");

  const num = parseFloat(integerPart);

  if (isNaN(num)) return "-";

  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum >= 1e9) {
    return `${sign}$${(absNum / 1e9).toFixed(2)}B`;
  } else if (absNum >= 1e6) {
    return `${sign}$${(absNum / 1e6).toFixed(2)}M`;
  } else {
    return `${sign}$${absNum.toLocaleString()}`;
  }
}

// function formatNumber(input) {
//   if (!input) return "-";
//   let num = parseFloat(input?.replace(/[$,]/g, ""));
//   if (num >= 1e9) {
//     return `$ ${(num / 1e9).toFixed(2)}B`;
//   } else if (num >= 1e6) {
//     return `$ ${(num / 1e6).toFixed(2)}M`;
//   } else {
//     return `$ ${num.toLocaleString()}`;
//   }
// }

function formatPercentage(input) {
  if (!input || input == "-" || input == "-%") return "-";
  let num = parseFloat(input?.replace("%", ""));

  if (isNaN(num)) {
    return "-";
  } else if (num === 0) {
    return "0%";
  } else if (Math.abs(num) < 1) {
    // Keep significant digits so very small values (e.g. 0.000261) are not
    // rounded down to "0.000".
    return parseFloat(num.toPrecision(3)) + " %";
  } else {
    return num.toFixed(2) + " %";
  }
}

function formatTeachingStatus(status) {
  if (status == null || status === "-" || status === "") return "-";
  return ["1", 1, "Y", "Yes", true].includes(status) ? "Yes" : "No";
}

function emptyToDash(value) {
  if (value == null || value === "" || value === "-") return "-";
  return value;
}

function formatQualityValue(value) {
  if (
    value == null ||
    value === "-" ||
    value === "" ||
    value === "None" ||
    value === "None%"
  )
    return "N/A";
  return value;
}

function formatAreaSmart(input) {
  if (!input || input == "-") return "-";
  let area = parseInt(input.replace(/\D/g, ""), 10);

  const oneAcre = 43560; // 1 acre = 43,560 sq. ft.

  if (area >= oneAcre) {
    return (area / oneAcre).toFixed(2) + " acres";
  } else {
    return area.toLocaleString() + " sq. ft.";
  }
}

function addCommasToNumber(num) {
  if (num == null || num === "") return num;
  const numStr = String(num).replace(/,/g, '');

  // Check if it's a valid number
  if (!/^-?\d+$/.test(numStr)) return num; // Return original if not a number

  // Add commas (handles negative numbers)
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function splitAndClean(text) {
  if(!text || text == "-") return [];
  return text
    .split(",") // Split by newline
    .map(item => item.trim().replace(/,$/, "")) // Trim spaces & remove trailing comma
    .filter(item => item !== ""); // Remove empty entries
}

export function getHighestValueIndex(dataArray, keyPath) {
  if (!Array.isArray(dataArray) || !keyPath) return -1;

  const getValueByPath = (obj, path) =>
    path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

  const indexedValues = dataArray.map((item, index) => {
    const raw = getValueByPath(item, keyPath);

    if (typeof raw !== 'string' && typeof raw !== 'number') {
      return null; // Invalid or missing value
    }

    let num = 0;
    if (typeof raw === 'string') {
      // Early exit if it's clearly a placeholder like "-"
      if (raw.trim() === "-" || raw.trim() === "") return null;
      num = parseFloat(raw.replace(/[$,]/g, ''));
    } else if (typeof raw === 'number') {
      num = raw;
    }

    return !isNaN(num) ? { index, value: num } : null;
  }).filter(Boolean);

  if (indexedValues.length === 0) return -1;

  const { index: maxIndex } = indexedValues.reduce((maxItem, currentItem) =>
    currentItem.value > maxItem.value ? currentItem : maxItem
  );

  return maxIndex;
}